import { useEffect, useRef } from 'react';

// Esc closes the overlay — and closes ONLY the topmost one.
//
// Extracted because the app had three different dismissal grammars at once: RdCardDialog and Drawer
// supported Esc + scrim + ✕, while ConflictResolveModal and MeetingPrepModal — the two dialogs you
// spend the MOST time in — supported scrim + ✕ only, so the habit you learn on one modal silently
// fails on the next.
//
// The stack exists because Sources now deliberately keeps one overlay mounted BEHIND another: the
// Devices drawer stays open under the setup guide, and the Manage drawer stays open under the
// Pause/Disconnect confirm, so that Done and Cancel return there instead of dumping the user on the
// bare Sources grid. Each overlay registering its own window listener was fine while only one could
// ever be open; with two open, a single Escape fired both and collapsed the whole stack — undoing
// exactly what that mounting buys. Registration order IS the stack, so the last overlay to open is
// the only one Escape closes.
//
// `active` lets an always-mounted overlay (Drawer renders its scrim/aside even when closed) join
// and leave the stack as it opens and closes. It is the ONLY dependency of the registering effect
// on purpose: `onClose` is nearly always an inline arrow, so depending on it would re-register on
// every parent render and shuffle this overlay to the top of a stack it does not own. The callback
// is read through a ref instead, so it is always current without moving the overlay's position.
//
// Note this still does NOT put the overlay in browser history; hardware/browser Back leaves the
// surface rather than closing the overlay. That fix is deliberately deferred: pushState would
// overwrite React Router's {usr, key, idx} history state, which navigate(-1) depends on.
const openOverlays: symbol[] = [];

export function useEscapeToClose(onClose: () => void, active = true) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!active) return;
    const token = Symbol('overlay');
    openOverlays.push(token);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Someone else is on top; this overlay is not the one being dismissed.
      if (openOverlays[openOverlays.length - 1] !== token) return;
      onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const i = openOverlays.lastIndexOf(token);
      if (i !== -1) openOverlays.splice(i, 1);
    };
  }, [active]);
}
